/**
 * authInterceptor.js
 * Global fetch interceptor for 401 session-expiry handling.
 *
 * Monkey-patches window.fetch so that ANY API call returning 401
 * clears stored credentials and redirects to the correct login page.
 *
 * Exclusions:
 *  - Login endpoint POSTs (wrong-password 401s must reach the caller)
 *  - Already on a login/register page (no redirect loop)
 *  - Redirect already in progress (multiple in-flight 401s)
 *
 * Imported once in index.js before the React tree mounts.
 */

let isRedirectingFor401 = false;

// Login endpoints return 401 for invalid credentials — must NOT trigger redirect
const LOGIN_ENDPOINTS = [
  '/api/admin/auth/login',
  '/api/interview/portal/validate',  // Portal passcode validation returns 401 for wrong passcode
];

// Pages where we should never redirect (already at a login/registration page)
const LOGIN_PAGES = ['/login', '/admin/login'];

// Paths that handle their own auth — never redirect from these
const SELF_AUTH_PATHS = ['/interview/portal/', '/sourcing-portal'];

// API routes with their own session (Lane 1331, the sourcing supplier portal).
// A 401 from them says nothing about the buyer session: the portal clears its
// own token, and the buyer's stored credentials must never be touched.
const SELF_AUTH_API_PREFIXES = ['/api/sourcing-portal/'];

function isSelfAuthEndpoint(url) {
  return SELF_AUTH_API_PREFIXES.some(prefix => url.includes(prefix));
}

function isLoginEndpoint(url) {
  return LOGIN_ENDPOINTS.some(ep => url.includes(ep));
}

function isOnLoginPage() {
  const path = window.location.pathname;
  if (LOGIN_PAGES.some(page => path === page || path === page + '/')) return true;
  // Portal pages handle their own auth — never redirect from them
  if (SELF_AUTH_PATHS.some(prefix => path.startsWith(prefix))) return true;
  return false;
}

function getTargetLoginPage() {
  const path = window.location.pathname;
  if (path.includes('/admin/')) return '/admin/login';
  return '/login';
}

function clearStoredAuth() {
  localStorage.removeItem('authToken');
  localStorage.removeItem('authUser');
  // Legacy keys still referenced in some pages
  localStorage.removeItem('token');
  localStorage.removeItem('user');
}

// Preserve the real fetch before patching
const originalFetch = window.fetch.bind(window);

window.fetch = async function (...args) {
  const response = await originalFetch(...args);

  if (response.status === 401) {
    const requestUrl = typeof args[0] === 'string' ? args[0] : (args[0]?.url || '');

    if (!isLoginEndpoint(requestUrl) && !isSelfAuthEndpoint(requestUrl)
        && !isOnLoginPage() && !isRedirectingFor401) {
      isRedirectingFor401 = true;
      clearStoredAuth();
      window.location.href = getTargetLoginPage();
    }
  }

  return response;
};
